<template>
	<div class="guest-list-selection">
		<CCallout type="info">
			<p>Please continue to access {{ orgName }}.</p>
		</CCallout>
		<ValidationObserver ref="observer" tag="form" class="social-login__form" novalidate @submit.prevent="handleClick">
			<div v-if="showRegistrationForms">
				<p class="text-center mt-1 mb-3 font-bold">How would you like to register?</p>
				<ElRadioGroup
					v-model="selectedFormId"
					class="c-radio-fancy mr-3 grid"
					@change="clearRegistrationFormsError"
				>
					<ElRadioButton
						v-for="form in registrationForms"
						:key="form.Id"
						:label="form.Id"
						class="pointer"
					>
						<div class="guest-list-selection__title ml-2 inline-block align-top">{{ form.Title }}</div>
					</ElRadioButton>
				</ElRadioGroup>
				<CCallout v-if="showRegistrationFormsError" type="error" size="s">
					Please select a registration form.
				</CCallout>
				<CCallout v-if="showInactiveRecordError" type="error" size="s">
					You are not an active guest of {{ orgName }}.
				</CCallout>
			</div>
			<!-- @vue-ignore ref -->
			<CValidatedInput
				v-if="requiresConsent"
				ref="termsCheckbox"
				v-model="termsAccepted"
				type="checkbox"
				class="sz-2"
				rules="accept-portal-terms"
			>
				I agree to the <a v-if="internalTosLink" target="_blank" :href="internalTosLink">Terms of Service</a>{{ hasBothLinks ? ' & ' : '' }}<a v-if="internalPrivacyPolicyLink" target="_blank" :href="internalPrivacyPolicyLink">Privacy Policy</a> provided by {{ orgName }}.
			</CValidatedInput>
			<!-- @vue-ignore type -->
			<CStatusButton class="button button--primary sz-4" type="submit">
				Continue
			</CStatusButton>
		</ValidationObserver>
	</div>
</template>

<script lang="ts">
	import type { PropType } from 'vue';
	import { computed, defineComponent, ref } from 'vue';
	import { RadioGroup, RadioButton } from '@cognitoforms/element-ui';
	import CStatusButton from '@cognitoforms/shared-components/src/components/interaction/StatusButton.vue';
	import CValidatedInput from '@cognitoforms/shared-components/src/components/form-controls/ValidatedInput.vue';
	import CCallout from '@cognitoforms/shared-components/src/components/content/Callout.vue';
	import { extend, ValidationObserver } from 'vee-validate';
	import type { GuestRegistrationResult } from '@cognitoforms/types/server-types/services/infrastructure/authentication/guest-registration-result';

	extend('accept-portal-terms', {
		validate: value => !!value,
		message: 'You must agree.'
	});

	export default defineComponent({
		components: {
			CStatusButton,
			CValidatedInput,
			CCallout,
			ValidationObserver,
			ElRadioGroup: RadioGroup,
			ElRadioButton: RadioButton
		},
		props: {
			authResult: {
				type: Object as PropType<GuestRegistrationResult>,
				required: true
			}
		},
		emits: ['continue'],
		setup(props, { emit }) {
			const orgName = props.authResult.OrganizationName;

			const registrationForms = props.authResult.RegistrationForms.toSorted((a, b) => (a.Title).localeCompare(b.Title));
			const showRegistrationForms = registrationForms.length !== 1;

			// Default to the first form if only one is available
			const selectedFormId = ref<string>(showRegistrationForms ? null : registrationForms[0].Id);

			const internalTosLink = ref<string>(props.authResult.TermsOfServiceLink);
			const internalPrivacyPolicyLink = ref<string>(props.authResult.PrivacyPolicyLink);
			const requiresConsent = computed(() => props.authResult.RequiresConsent);
			const hasBothLinks = computed(() => !!internalTosLink.value && !!internalPrivacyPolicyLink.value);

			const termsAccepted = ref<boolean>(false);
			const observer = ref<InstanceType<typeof ValidationObserver>>(null);

			const showRegistrationFormsError = ref<boolean>(false);
			const showInactiveRecordError = ref<boolean>(false);

			const handleClick = () => {
				if (!termsAccepted.value && requiresConsent.value) {
					observer.value.validate();
					return;
				}
				if (showRegistrationForms && !selectedFormId.value) {
					showRegistrationFormsError.value = true;
					return;
				}

				const formInfo = props.authResult.RegistrationForms.find(f => f.Id === selectedFormId.value);
				if (formInfo.HasInactiveRecord && formInfo.EntryToken === null) {
					showInactiveRecordError.value = true;
					return;
				}

				emit('continue', formInfo);
			};

			const clearRegistrationFormsError = () => {
				showRegistrationFormsError.value = false;
				showInactiveRecordError.value = false;
			};

			return {
				orgName,
				registrationForms,
				showRegistrationForms,
				selectedFormId,
				internalTosLink,
				internalPrivacyPolicyLink,
				requiresConsent,
				hasBothLinks,
				termsAccepted,
				observer,
				showRegistrationFormsError,
				showInactiveRecordError,
				handleClick,
				clearRegistrationFormsError
			};
		}
	});
</script>

<style lang="scss">
.guest-list-selection {

	&__title {
		// 100% width - radio button margin and width - gap
		max-width: calc(100% - 22px - $space__2);
	}

	.el-radio-button__inner {
		width: 100%;
	}
}
</style>